// Mock data for Chaturvedi Lab website

export const labInfo = {
  name: "The Chaturvedi Lab",
  institution: "UC Riverside",
  tagline: "Systems, Synthetic & Quantitative Biology Lab",
  mission: "We Study Design Principles in Biology to Engineer the Future of Medicine"
};

export const heroContent = {
  title: "Welcome to The Chaturvedi Lab",
  subtitle: "Systems, Synthetic & Quantitative Biology Lab",
  description: "We Study Design Principles in Biology to Engineer the Future of Medicine",
  backgroundImage: "https://images.pexels.com/photos/35967917/pexels-photo-35967917.png"
};

export const aboutContent = {
  description: "The Chaturvedi Lab at UC Riverside investigates how complex biological systems make decisions, and how those decisions can be modeled, engineered, and ultimately directed toward better clinical outcomes. We take a systems and synthetic biology approach to questions at the interface of development, oncogenesis, and host–disease interactions, asking how cellular and molecular circuits are wired, how they adapt, and how they can be reprogrammed.",
  
  approach: "Our group develops both experimental and computational platforms to make these questions tractable. We build synthetic circuits to probe network logic, engineer single-cell technologies that capture decision-making in real time, and create predictive frameworks that integrate machine learning with emerging tools in quantum computing. These methods allow us to study biology as a dynamic, information-rich system and to uncover rules that generalize across contexts.",
  
  translational: "The translational potential of our research is equally central. We are applying these principles with our clinical partners to design therapies with stronger barriers to resistance, to develop strategies that make transplantation safer, and to improve clinical outcomes in populations living with chronic infection. In all cases, our aim is to treat biology not as a collection of pathways, but as a system of principles that can be revealed, modeled, and engineered to create new possibilities for medicine."
};

export const researchAreas = [
  {
    id: 1,
    title: "Systems Biology",
    description: "Investigating how complex biological systems make decisions through computational and experimental platforms.",
    icon: "Network",
    image: "https://images.unsplash.com/photo-1678845536613-5cf0ec5245cd"
  },
  {
    id: 2,
    title: "Synthetic Circuits",
    description: "Building synthetic circuits to probe network logic and understand biological decision-making processes.",
    icon: "CircuitBoard",
    image: "https://images.unsplash.com/photo-1648792940059-3b782a7b8b20"
  },
  {
    id: 3,
    title: "Single-Cell Technologies",
    description: "Engineering technologies that capture cellular decision-making in real time at single-cell resolution.",
    icon: "Microscope",
    image: "https://images.pexels.com/photos/8940473/pexels-photo-8940473.jpeg"
  },
  {
    id: 4,
    title: "Machine Learning & Quantum Computing",
    description: "Creating predictive frameworks integrating machine learning with emerging quantum computing tools.",
    icon: "Brain",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb"
  },
  {
    id: 5,
    title: "Translational Medicine",
    description: "Designing therapies with stronger barriers to resistance for better clinical outcomes.",
    icon: "HeartPulse",
    image: "https://images.unsplash.com/photo-1582719471384-894fbb16e074"
  },
  {
    id: 6,
    title: "Host-Disease Interactions",
    description: "Studying cellular and molecular circuits in development, oncogenesis, and disease interactions.",
    icon: "Activity",
    image: "https://images.unsplash.com/photo-1707079347080-1108e559f599"
  }
];

export const fundingSources = [
  {
    id: 1,
    name: "National Institutes of Health",
    logo: "/logos/nih_logo.svg",
    alt: "NIH"
  },
  {
    id: 2,
    name: "California Institute for Regenerative Medicine",
    logo: "/logos/cirm_logo.svg",
    alt: "CIRM"
  },
  {
    id: 3,
    name: "National Institute on Drug Abuse",
    logo: "/logos/nida_logo.svg",
    alt: "NIDA"
  },
  {
    id: 4,
    name: "National Institute of General Medical Sciences",
    logo: "/logos/nigms_logo.svg",
    alt: "NIGMS"
  }
];

export const collaborators = [
  {
    id: 1,
    name: "UC San Francisco",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/UCSF_logo.svg/320px-UCSF_logo.svg.png"
  },
  {
    id: 2,
    name: "University of California, Los Angeles",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/The_University_of_California_UCLA.svg/320px-The_University_of_California_UCLA.svg.png"
  },
  {
    id: 3,
    name: "Loma Linda University Medical School",
    logo: "https://upload.wikimedia.org/wikipedia/en/thumb/4/47/Loma_Linda_University_seal.svg/200px-Loma_Linda_University_seal.svg.png"
  },
  {
    id: 4,
    name: "Gladstone Institutes",
    logo: "https://gladstone.org/sites/default/files/Gladstone_Logo_Brandmark.svg"
  },
  {
    id: 5,
    name: "Harvard University",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cc/Harvard_University_coat_of_arms.svg/200px-Harvard_University_coat_of_arms.svg.png"
  },
  {
    id: 6,
    name: "Emory University",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Emory_University_Seal.svg/200px-Emory_University_Seal.svg.png"
  },
  {
    id: 7,
    name: "UC San Diego",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/UC_San_Diego_logo.svg/320px-UC_San_Diego_logo.svg.png"
  },
  {
    id: 8,
    name: "University of Southern California",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c7/USClogo.svg/320px-USClogo.svg.png"
  },
  {
    id: 9,
    name: "Indian Institute of Science",
    logo: "https://upload.wikimedia.org/wikipedia/en/thumb/5/5d/Indian_Institute_of_Science_Logo.svg/200px-Indian_Institute_of_Science_Logo.svg.png"
  },
  {
    id: 10,
    name: "Stanford University",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Stanford_University_seal_2003.svg/200px-Stanford_University_seal_2003.svg.png"
  }
];

export const galleryImages = [
  {
    id: 1,
    url: "https://images.pexels.com/photos/35967917/pexels-photo-35967917.png",
    alt: "DNA sequencing visualization"
  },
  {
    id: 2,
    url: "https://images.pexels.com/photos/25626587/pexels-photo-25626587.jpeg",
    alt: "Molecular structure analysis"
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1648792940059-3b782a7b8b20",
    alt: "DNA double helix"
  },
  {
    id: 4,
    url: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb",
    alt: "Computational biology"
  },
  {
    id: 5,
    url: "https://images.unsplash.com/photo-1582719471384-894fbb16e074",
    alt: "Medical research technology"
  },
  {
    id: 6,
    url: "https://images.unsplash.com/photo-1628595351029-c2bf17511435",
    alt: "Laboratory equipment"
  }
];

export const contactInfo = {
  email: "sonalic@ucr.edu",
  address: "University of California, Riverside",
  city: "Riverside, CA 92521"
};
